import {
  parseHuddleRealtimeToolCallEvents,
  startHuddleRealtimeSession,
} from "@/domains/huddle/huddle-realtime-client";
import { HUDDLE_MODE_START_EVENT } from "@/domains/huddle/huddle-mode-events";
import type {
  HuddleRealtimeConnectionStatus,
  HuddleRealtimeSessionHandle,
  HuddleRealtimeToolCallEvent,
  HuddleRealtimeTranscriptEvent,
} from "@/domains/huddle/huddle-realtime-client";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

type HuddleLauncherState = "idle" | "starting" | "active" | "ending" | "error";

type HuddleActivityListItem =
  | (HuddleRealtimeTranscriptEvent & {
      id: string;
      kind: "transcript";
    })
  | (HuddleRealtimeToolCallEvent & {
      kind: "tool-call";
    });

type HuddleTranscriptListItem = Extract<HuddleActivityListItem, { kind: "transcript" }>;

type HuddleToolCallListItem = Extract<HuddleActivityListItem, { kind: "tool-call" }>;

type HuddleActivityItemKind = HuddleActivityListItem["kind"];

interface HuddleModeLauncherProps {
  onStartRequest?: () => void;
}

const HUDDLE_CONTROL_ICON_SIZE = 32;
const HUDDLE_CONTROL_BAR_HEIGHT = 58;
const HUDDLE_CONTROL_COLORS = {
  mute: "#4F73E8",
  end: "#D91E0F",
} as const;

export const HuddleModeLauncher = observer(function HuddleModeLauncher({
  onStartRequest,
}: HuddleModeLauncherProps) {
  const { store } = useAppStore();
  const [launcherState, setLauncherState] = useState<HuddleLauncherState>("idle");
  const [connectionStatus, setConnectionStatus] = useState<HuddleRealtimeConnectionStatus>("ended");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState(false);
  const [activityItems, setActivityItems] = useState<HuddleActivityListItem[]>([]);
  const sessionHandleRef = useRef<HuddleRealtimeSessionHandle | null>(null);
  const isStartingHuddleRef = useRef(false);
  const toolCallEventIdsRef = useRef<Set<string>>(new Set());
  const shouldShowLauncher = store.spaceAccountFeatureFlags.getFlagStatus("huddle-mode") ?? false;

  const startHuddle = useCallback(async () => {
    if (
      isStartingHuddleRef.current ||
      launcherState === "starting" ||
      launcherState === "active" ||
      launcherState === "ending"
    ) {
      return;
    }

    isStartingHuddleRef.current = true;
    setErrorMessage(null);
    setIsMicrophoneMuted(false);
    setActivityItems([]);
    toolCallEventIdsRef.current = new Set();
    setLauncherState("starting");
    setConnectionStatus("requesting-microphone");

    try {
      const sessionHandle = await startHuddleRealtimeSession({
        onRealtimeEvent: realtimeEvent => {
          const toolCallItems = buildNewToolCallItems({
            seenToolCallEventIds: toolCallEventIdsRef.current,
            toolCallEvents: parseHuddleRealtimeToolCallEvents(realtimeEvent),
          });

          if (toolCallItems.length > 0) {
            setActivityItems(currentActivityItems => [...currentActivityItems, ...toolCallItems]);
          }
        },
        onStatusChange: setConnectionStatus,
        onTranscript: transcriptEvent => {
          setActivityItems(currentActivityItems => [
            ...currentActivityItems,
            {
              ...transcriptEvent,
              id: createTranscriptId(),
              kind: "transcript",
            },
          ]);
        },
      });

      sessionHandleRef.current = sessionHandle;
      sessionHandle.setMicrophoneMuted(false);
      setLauncherState("active");
    } catch (error) {
      sessionHandleRef.current = null;
      setErrorMessage(getErrorMessage(error));
      setConnectionStatus("ended");
      setLauncherState("error");
    } finally {
      isStartingHuddleRef.current = false;
    }
  }, [launcherState]);

  useEffect(() => {
    return () => {
      void sessionHandleRef.current?.stop().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    if (!shouldShowLauncher) {
      return;
    }

    const handleStartRequest = () => {
      onStartRequest?.();
      void startHuddle();
    };

    window.addEventListener(HUDDLE_MODE_START_EVENT, handleStartRequest);
    return () => {
      window.removeEventListener(HUDDLE_MODE_START_EVENT, handleStartRequest);
    };
  }, [onStartRequest, shouldShowLauncher, startHuddle]);

  if (!shouldShowLauncher || launcherState === "idle") {
    return null;
  }

  const endHuddle = async () => {
    if (launcherState === "ending") {
      return;
    }

    const sessionHandle = sessionHandleRef.current;
    sessionHandleRef.current = null;
    setLauncherState("ending");

    if (!sessionHandle) {
      setConnectionStatus("ended");
      setIsMicrophoneMuted(false);
      setLauncherState("idle");
      return;
    }

    try {
      await sessionHandle.stop();
      setConnectionStatus("ended");
      setIsMicrophoneMuted(false);
      setLauncherState("idle");
    } catch (error) {
      setConnectionStatus("ended");
      setIsMicrophoneMuted(false);
      setErrorMessage(getErrorMessage(error));
      setLauncherState("error");
    }
  };

  const toggleMicrophoneMuted = () => {
    if (launcherState !== "active") {
      return;
    }

    const sessionHandle = sessionHandleRef.current;
    if (!sessionHandle) {
      return;
    }

    const nextIsMicrophoneMuted = !isMicrophoneMuted;
    sessionHandle.setMicrophoneMuted(nextIsMicrophoneMuted);
    setIsMicrophoneMuted(nextIsMicrophoneMuted);
  };

  const dismissError = () => {
    setErrorMessage(null);
    setIsMicrophoneMuted(false);
    setLauncherState("idle");
  };

  const isError = launcherState === "error";

  return (
    <OverlayContainer>
      {isError ? (
        <>
          <OverlayHeader>
            <div>
              <Title>Huddle Mode</Title>
              <StatusLine>Error</StatusLine>
            </div>
            <MDSButton
              size={MDSButtonSize.Small}
              variant={MDSButtonVariant.Default}
              onPress={dismissError}
            >
              Close
            </MDSButton>
          </OverlayHeader>
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </>
      ) : (
        <>
          <StatusPill>
            {formatHuddleStatus({
              connectionStatus,
              isMicrophoneMuted,
              launcherState,
            })}
          </StatusPill>

          <ActivityList>
            {activityItems.length === 0 ? (
              <EmptyTranscriptMessage variant={MDSTextVariant.TextS}>
                {formatEmptyActivityMessage({ launcherState, connectionStatus })}
              </EmptyTranscriptMessage>
            ) : (
              activityItems.map(activityItem => (
                <ActivityItem key={activityItem.id} $kind={activityItem.kind}>
                  {activityItem.kind === "tool-call" ? (
                    <ToolCallText>
                      Calling <ToolName>{activityItem.name}</ToolName>
                    </ToolCallText>
                  ) : (
                    <TranscriptText $speaker={activityItem.speaker}>
                      {activityItem.text}
                    </TranscriptText>
                  )}
                </ActivityItem>
              ))
            )}
          </ActivityList>

          <CallControls
            initial={{
              opacity: 0,
              scaleX: 0.82,
              x: 52,
            }}
            animate={{
              opacity: 1,
              scaleX: 1,
              x: 0,
            }}
            transition={{
              opacity: { duration: 0.14 },
              scaleX: { duration: 0.22, ease: [0.2, 0, 0, 1] },
              x: { type: "spring", stiffness: 420, damping: 34 },
            }}
          >
            <ControlButton
              aria-label={isMicrophoneMuted ? "Unmute microphone" : "Mute microphone"}
              aria-pressed={isMicrophoneMuted}
              $tone="mute"
              isDisabled={launcherState !== "active"}
              size={MDSButtonSize.Large}
              variant={MDSButtonVariant.Default}
              onPress={toggleMicrophoneMuted}
            >
              <ControlIcon
                slot={MDSButtonSlotName.LeftIcon}
                kind={isMicrophoneMuted ? MDSIconKind.Microphone : MDSIconKind.MicrophoneSlash}
                size={HUDDLE_CONTROL_ICON_SIZE}
                boxSize={HUDDLE_CONTROL_ICON_SIZE}
                overrideColor={HUDDLE_CONTROL_COLORS.mute}
              />
              {isMicrophoneMuted ? "Unmute" : "Mute"}
            </ControlButton>
            <ControlButton
              aria-label="End Huddle"
              $tone="end"
              isDisabled={launcherState === "starting"}
              isLoading={launcherState === "ending"}
              size={MDSButtonSize.Large}
              variant={MDSButtonVariant.Default}
              onPress={endHuddle}
            >
              <EndControlIcon
                slot={MDSButtonSlotName.LeftIcon}
                kind={MDSIconKind.Phone}
                size={HUDDLE_CONTROL_ICON_SIZE}
                boxSize={HUDDLE_CONTROL_ICON_SIZE}
                overrideColor={HUDDLE_CONTROL_COLORS.end}
              />
              End
            </ControlButton>
          </CallControls>
        </>
      )}
    </OverlayContainer>
  );
});

const buildNewToolCallItems = ({
  seenToolCallEventIds,
  toolCallEvents,
}: {
  seenToolCallEventIds: Set<string>;
  toolCallEvents: HuddleRealtimeToolCallEvent[];
}): HuddleToolCallListItem[] => {
  return toolCallEvents.flatMap(toolCallEvent => {
    if (seenToolCallEventIds.has(toolCallEvent.id)) {
      return [];
    }

    seenToolCallEventIds.add(toolCallEvent.id);

    return [
      {
        ...toolCallEvent,
        kind: "tool-call",
      },
    ];
  });
};

const createTranscriptId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
};

const formatConnectionStatus = (status: HuddleRealtimeConnectionStatus) => {
  switch (status) {
    case "requesting-microphone":
      return "Requesting microphone";
    case "connecting":
      return "Connecting";
    case "connected":
      return "Live";
    case "disconnected":
      return "Disconnected";
    case "ended":
      return "Ended";
  }
};

const formatEmptyActivityMessage = ({
  connectionStatus,
  launcherState,
}: {
  connectionStatus: HuddleRealtimeConnectionStatus;
  launcherState: HuddleLauncherState;
}) => {
  if (launcherState === "starting" || connectionStatus === "requesting-microphone") {
    return "Opening Huddle...";
  }

  if (connectionStatus === "connecting") {
    return "Connecting...";
  }

  return "Listening...";
};

const formatHuddleStatus = ({
  connectionStatus,
  isMicrophoneMuted,
  launcherState,
}: {
  connectionStatus: HuddleRealtimeConnectionStatus;
  isMicrophoneMuted: boolean;
  launcherState: HuddleLauncherState;
}) => {
  if (launcherState === "error") {
    return "Error";
  }

  const connectionStatusLabel = formatConnectionStatus(connectionStatus);

  if (!isMicrophoneMuted) {
    return connectionStatusLabel;
  }

  return `${connectionStatusLabel} - Muted`;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong while running Huddle.";
};

const OverlayContainer = styled.div({
  background:
    "linear-gradient(180deg, rgba(185, 204, 255, 0.98) 0%, rgba(222, 232, 255, 0.98) 58%, rgba(235, 242, 255, 0.98) 100%)",
  border: "1px solid rgba(255, 255, 255, 0.7)",
  borderRadius: "inherit",
  color: "#27263A",
  display: "flex",
  flexDirection: "column",
  gap: 18,
  height: "100%",
  inset: 0,
  overflow: "hidden",
  padding: "34px 34px 18px",
  position: "absolute",
  width: "100%",
  zIndex: 2,
});

const OverlayHeader = styled.div({
  alignItems: "flex-start",
  display: "flex",
  gap: 16,
  justifyContent: "space-between",
});

const Title = styled.div({
  color: "#27263A",
  fontSize: 18,
  fontWeight: 700,
});

const StatusLine = styled.div({
  color: "rgba(39, 38, 58, 0.62)",
  fontSize: 13,
  marginTop: 4,
});

const StatusPill = styled.div({
  alignSelf: "flex-start",
  backgroundColor: "rgba(255, 255, 255, 0.42)",
  border: "1px solid rgba(255, 255, 255, 0.5)",
  borderRadius: 999,
  color: "rgba(39, 38, 58, 0.58)",
  flexShrink: 0,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "18px",
  padding: "4px 10px",
});

const ActivityList = styled.div({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: 40,
  minHeight: 0,
  overflowY: "auto",
  padding: "8px 0 4px",
  scrollbarWidth: "none",

  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const EmptyTranscriptMessage = styled(MDSText)({
  color: "rgba(39, 38, 58, 0.42)",
  fontSize: 42,
  fontWeight: 700,
  lineHeight: 1.12,
});

const ActivityItem = styled.div<{ $kind: HuddleActivityItemKind }>(({ $kind }) => ({
  display: "block",
  marginTop: $kind === "tool-call" ? -12 : 0,
}));

const TranscriptText = styled.div<{ $speaker: HuddleTranscriptListItem["speaker"] }>(
  ({ $speaker }) => ({
    color: $speaker === "assistant" ? "rgba(66, 63, 88, 0.48)" : "rgba(39, 38, 58, 0.84)",
    fontSize: 38,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1.12,
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
  })
);

const ToolCallText = styled.div({
  color: "rgba(39, 38, 58, 0.52)",
  fontFamily:
    'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
  fontSize: 15,
  letterSpacing: 0,
  lineHeight: 1.5,
});

const ToolName = styled.span({
  fontFamily: "inherit",
});

const CallControls = styled(motion.div)({
  boxSizing: "border-box",
  display: "grid",
  flexShrink: 0,
  gap: 12,
  gridTemplateColumns: "1fr 1fr",
  height: HUDDLE_CONTROL_BAR_HEIGHT,
  overflow: "visible",
  padding: 0,
  transformOrigin: "right center",
});

const ControlButton = styled(MDSButton)<{ $tone: "mute" | "end" }>(({ $tone }) => ({
  backgroundColor: "#FFFFFF",
  border: "1px solid rgba(255, 255, 255, 0.78)",
  borderRadius: 999,
  boxShadow: "0 12px 30px rgba(48, 64, 112, 0.12)",
  color: HUDDLE_CONTROL_COLORS[$tone],
  fontSize: 24,
  fontWeight: 800,
  lineHeight: "28px",
  gap: 12,
  height: "100%",
  justifyContent: "center",
  minWidth: 0,
  padding: "0 12px",

  "& .mds-icon, & .mds-icon svg": {
    color: HUDDLE_CONTROL_COLORS[$tone],
    fill: HUDDLE_CONTROL_COLORS[$tone],
    fontSize: HUDDLE_CONTROL_ICON_SIZE,
    height: HUDDLE_CONTROL_ICON_SIZE,
    width: HUDDLE_CONTROL_ICON_SIZE,
  },

  "&[data-hovered]:not([data-disabled])": {
    backgroundColor: $tone === "end" ? "rgba(217, 30, 15, 0.06)" : "rgba(79, 115, 232, 0.08)",
    boxShadow: "0 14px 34px rgba(48, 64, 112, 0.16)",
  },

  "&[data-disabled]": {
    cursor: "not-allowed",
    opacity: 0.62,
  },
}));

const ControlIcon = styled(MDSIcon)({
  height: HUDDLE_CONTROL_ICON_SIZE,
  width: HUDDLE_CONTROL_ICON_SIZE,
});

const EndControlIcon = styled(ControlIcon)({
  transform: "rotate(135deg)",
});

const ErrorMessage = styled.div({
  backgroundColor: "rgba(255, 255, 255, 0.48)",
  border: "1px solid rgba(255, 255, 255, 0.56)",
  borderRadius: 24,
  color: "#7A211A",
  fontSize: 15,
  lineHeight: 1.45,
  padding: "16px 18px",
  whiteSpace: "pre-wrap",
});
